import React from 'react';
import styled from '@emotion/styled';
import Button from '@via-profit/ui-kit/Button';
import TextField from '@via-profit/ui-kit/TextField';
import Selectbox, { SelectboxItem } from '@via-profit/ui-kit/Selectbox';
import Switch from '@via-profit/ui-kit/Switch';

import SearchBox, { type SearchBoxPreset } from '../SearchBox/SearchBox';
import type { SearchArea } from '../../api/geocoder';
import { pointEndpoint } from '../../api/place';
import type { RouteUrlState } from '../../urlState';
import { isMobile, MOBILE_QUERY } from '../../layout';
import {
  BorderIcon,
  CarIcon,
  ChevronRightIcon,
  ListIcon,
  PinIcon,
  PlusIcon,
  SwapIcon,
  TruckIcon,
} from '../../../assets/icons';
import { ROUTE_TOLL_COLOR } from '../Map/MapView';
import type { GeocodeResult } from '../../api/types';
import {
  fetchRoute,
  RouteError,
  type BorderPolicy,
  type Route,
  type RouteProfile,
  type RouteRequest,
} from '../../api/routing';

/** Точка маршрута, заданная снаружи (кнопка "Маршрут" в подсказке, выбор на
 *  карте); target - id поля или 'last' (поле "Куда"). Новый объект
 *  перезаписывает то, что было в поле. */
export interface RoutePointPreset extends SearchBoxPreset {
  readonly target: string;
}

export interface RoutePanelProps {
  readonly near?: SearchArea | null;
  /** Выбранный маршрут (или null - маршрута нет), координаты [lat, lon]
   *  точек по порядку (null - точка не задана) и невыбранные варианты -
   *  карта рисует их серым. */
  readonly onRouteChange: (
    route: Route | null,
    points: ([number, number] | null)[],
    others: readonly Route[],
  ) => void;
  readonly preset?: RoutePointPreset | null;
  /** Выбор точки на карте: для какого поля и на каком шаге - 'picking' ждём
   *  клика по карте, 'resolving' определяем адрес точки. */
  readonly pick?: { readonly target: string; readonly state: 'picking' | 'resolving' } | null;
  /** Кнопка выбора на карте у поля target - включает/выключает режим выбора. */
  readonly onPickToggle?: (target: string) => void;
  /** Состояние при открытии (из ссылки или с прошлого раза); читается один раз. */
  readonly initial?: RouteUrlState | null;
  /** Точки и параметры изменились - для ссылки на маршрут. */
  readonly onStateChange?: (state: RouteUrlState) => void;
}

/** Не больше стольких точек вместе с началом и концом - как у Яндекс.Карт. */
const MAX_POINTS = 10;

interface BorderOption {
  readonly value: BorderPolicy;
  readonly label: string;
}

const BORDER_OPTIONS: readonly BorderOption[] = [
  { value: 'AVOID', label: 'Не заезжать в другие страны без необходимости' },
  { value: 'FORBID', label: 'Запретить транзит через другие страны' },
  { value: 'ALLOW', label: 'Кратчайший путь, границы не важны' },
];

// Всё интерактивное - компоненты ui-kit (Button, TextField,
// Selectbox, Switch); здесь только раскладка и текст, цвета - из темы ui-kit.
const Panel = styled.div`
  max-height: calc(100vh - 140px);
  overflow-y: auto;

  @media ${MOBILE_QUERY} {
    max-height: 60vh;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  cursor: pointer;
`;

const SummaryText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const PointHint = styled.div`
  margin: -4px 0 0 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.textSecondary.toString()};
`;

const PointError = styled(PointHint)`
  color: ${({ theme }) => theme.color.error.toString()};
`;

const FieldRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;

  & > :first-child {
    flex: 1;
    min-width: 0;
  }
`;

/** Кружок-кольцо точки маршрута внутри поля, как маркер на карте. svg, а не
 *  span: поле ui-kit даёт отступы по краям только svg-иконкам. */
const PointDot: React.FC<{ color: string }> = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <circle cx="7" cy="7" r="5" fill="none" stroke={color} strokeWidth="3" />
  </svg>
);

const Row = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;

  & > * {
    flex: 1;
  }
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.color.textSecondary.toString()};
`;

const Headline = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary.toString()};
`;

const Result = styled.div`
  border-top: 1px solid ${({ theme }) => theme.color.textSecondary.alpha(0.2).toString()};
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TollLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.color.textSecondary.toString()};
`;

const TollSwatch = styled.span`
  display: inline-block;
  width: 18px;
  height: 4px;
  border-radius: 2px;
  background: ${ROUTE_TOLL_COLOR};
`;

const ErrorText = styled(Result)`
  color: ${({ theme }) => theme.color.error.toString()};
`;

const StepsButton = styled(Button)`
  margin-top: 8px;
  justify-content: flex-start;
  color: ${({ theme }) => theme.color.textPrimary.toString()};

  & > span:nth-of-type(2) {
    flex: 1;
    text-align: left;
  }
`;

const StepsChevron = styled(ChevronRightIcon, { shouldForwardProp: prop => prop !== 'open' })<{ open: boolean }>`
  transform: rotate(${p => (p.open ? 90 : 0)}deg);
  transition: transform 150ms;
`;

const Maneuvers = styled.ol`
  margin: 4px 0 0;
  padding-left: 20px;
  max-height: 240px;
  overflow-y: auto;

  li {
    margin-bottom: 4px;
  }
`;

export function formatDuration(totalSec: number): string {
  const totalMin = Math.round(totalSec / 60);
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  if (hours === 0) return `${minutes} мин`;
  return minutes === 0 ? `${hours} ч` : `${hours} ч ${minutes} мин`;
}

function formatKm(km: number): string {
  return km < 10 ? `${km.toFixed(1)} км` : `${Math.round(km).toLocaleString('ru-RU')} км`;
}

/** Пустое поле - параметр не задан; иначе положительное число. */
function parsePositive(value: string): number | undefined {
  const n = Number(value.replace(',', '.'));
  return value.trim() !== '' && Number.isFinite(n) && n > 0 ? n : undefined;
}

type Endpoint = GeocodeResult | null;

interface RoutePoint {
  /** Ключ поля: при перестановке и удалении точек поле с введённым текстом
   *  переезжает вместе с точкой. */
  readonly id: string;
  readonly value: Endpoint;
  /** Подставленное снаружи значение - для текста в поле. */
  readonly preset: SearchBoxPreset | null;
}

const toLatLon = (point: Endpoint): [number, number] | null =>
  point && point.latitude != null && point.longitude != null ? [point.latitude, point.longitude] : null;

const POINT_COLORS = { start: '#1e7e34', via: '#1a73e8', end: '#e11d48' };

const RoutePanel: React.FC<RoutePanelProps> = ({
  near,
  onRouteChange,
  preset,
  pick,
  onPickToggle,
  initial,
  onStateChange,
}) => {
  const [points, setPoints] = React.useState<RoutePoint[]>(() => {
    const fromInitial = (initial?.points ?? [null, null]).map((point, i): RoutePoint => {
      const value = point ? pointEndpoint(point.lat, point.lon, point.label) : null;
      return { id: `p${i}`, value, preset: value && point ? { result: value, label: point.label } : null };
    });
    // кнопка "Маршрут" в подсказке открыла вкладку - её точка в "Куда"
    if (preset?.target === 'last') {
      fromInitial[fromInitial.length - 1] = { id: fromInitial[fromInitial.length - 1].id, value: preset.result, preset };
    }
    return fromInitial;
  });
  const nextIdRef = React.useRef(points.length);
  const [profile, setProfile] = React.useState<RouteProfile>(initial?.profile ?? 'truck');
  const [height, setHeight] = React.useState(initial?.height ?? '');
  const [weight, setWeight] = React.useState(initial?.weight ?? '');
  const [axleLoad, setAxleLoad] = React.useState(initial?.axleLoad ?? '');
  const [borders, setBorders] = React.useState<BorderPolicy>(initial?.borders ?? 'AVOID');
  const [avoidTolls, setAvoidTolls] = React.useState(initial?.avoidTolls ?? false);
  const [avoidFerries, setAvoidFerries] = React.useState(initial?.avoidFerries ?? false);

  const onStateChangeRef = React.useRef(onStateChange);
  onStateChangeRef.current = onStateChange;
  React.useEffect(() => {
    onStateChangeRef.current?.({
      points: points.map(point => {
        const lat = point.value?.latitude;
        const lon = point.value?.longitude;
        if (lat == null || lon == null) return null;
        // в поле - то, что подставили или выбрали; полный адрес - запасной
        const label = point.preset?.result === point.value ? point.preset.label : point.value!.fullAddress;
        return { lat, lon, label };
      }),
      profile,
      height,
      weight,
      axleLoad,
      borders,
      avoidTolls,
      avoidFerries,
    });
  }, [points, profile, height, weight, axleLoad, borders, avoidTolls, avoidFerries]);
  const [route, setRoute] = React.useState<Route | null>(null);
  const [variant, setVariant] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showManeuvers, setShowManeuvers] = React.useState(false);
  const [bordersOpen, setBordersOpen] = React.useState(false);
  // на телефоне панель закрывает полкарты - после построения маршрута и на
  // время выбора точки на карте она сворачивается в строку
  const [collapsed, setCollapsed] = React.useState(false);
  const isPicking = pick?.state === 'picking';
  React.useEffect(() => {
    if (isMobile()) setCollapsed(isPicking);
  }, [isPicking]);
  const abortRef = React.useRef<AbortController | null>(null);
  const onRouteChangeRef = React.useRef(onRouteChange);
  onRouteChangeRef.current = onRouteChange;

  React.useEffect(() => {
    if (!preset) return;
    setPoints(current => {
      const target = preset.target === 'last' ? current[current.length - 1].id : preset.target;
      return current.map(point =>
        point.id === target ? { ...point, value: preset.result, preset: { result: preset.result, label: preset.label } } : point,
      );
    });
  }, [preset]);

  const setPointValue = (id: string, value: Endpoint) =>
    setPoints(current => current.map(point => (point.id === id ? { ...point, value } : point)));

  const addPoint = () =>
    setPoints(current =>
      current.length >= MAX_POINTS
        ? current
        : [...current.slice(0, -1), { id: `p${nextIdRef.current++}`, value: null, preset: null }, current[current.length - 1]],
    );

  const removePoint = (id: string) => setPoints(current => current.filter(point => point.id !== id));

  // "Откуда" и "Куда" меняются местами, промежуточные - в обратном порядке
  const reversePoints = () => setPoints(current => [...current].reverse());

  const coords = points.map(point => toLatLon(point.value));
  const coordsKey = JSON.stringify(coords);
  const first = coords[0];
  const last = coords[coords.length - 1];

  const request: RouteRequest | null = React.useMemo(() => {
    if (!first || !last) return null;
    return {
      // промежуточные без координат (пустые поля) пропускаем
      points: coords.filter((c): c is [number, number] => c != null).map(([lat, lon]) => ({ lat, lon })),
      profile,
      truck:
        profile === 'truck'
          ? { height: parsePositive(height), weight: parsePositive(weight), axleLoad: parsePositive(axleLoad) }
          : undefined,
      borders,
      avoid: { tolls: avoidTolls, ferries: avoidFerries },
      // альтернативы бэкенд строит только между двумя точками
      alternatives: 2,
    };
    // coords - новый массив на каждый рендер, сравниваем по значениям
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordsKey, profile, height, weight, axleLoad, borders, avoidTolls, avoidFerries]);

  // маршрут пересчитывается сам при любом изменении точек или параметров;
  // параметры грузовика - текстовые поля, поэтому с небольшой задержкой
  React.useEffect(() => {
    abortRef.current?.abort();
    setError(null);

    if (!request) {
      setRoute(null);
      setIsLoading(false);
      onRouteChangeRef.current(null, coords, []);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    const timer = setTimeout(() => {
      setIsLoading(true);
      fetchRoute(request, controller.signal)
        .then(result => {
          setRoute(result);
          setVariant(0);
          if (isMobile()) setCollapsed(true);
        })
        .catch((err: unknown) => {
          if ((err as Error).name === 'AbortError') return;
          console.error('Route failed:', err);
          setRoute(null);
          setError(err instanceof RouteError ? err.message : 'Не удалось построить маршрут');
          onRouteChangeRef.current(null, coords, []);
        })
        .finally(() => {
          if (!controller.signal.aborted) setIsLoading(false);
        });
    }, 400);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // точки - тоже: пока задана одна, request остаётся null, а маркер на
    // карте всё равно должен переехать на новую
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, coordsKey]);

  // выбранный вариант - на карту; невыбранные - туда же, но серым
  const variants = React.useMemo(() => (route ? [route, ...route.alternatives] : []), [route]);
  const selected = variants[variant] ?? null;
  React.useEffect(() => {
    if (!selected) return;
    onRouteChangeRef.current(selected, coords, variants.filter(v => v !== selected));
    // coords - те же, что в запросе этого маршрута
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, variants]);

  const maneuvers = selected?.legs.flatMap(leg => leg.maneuvers) ?? [];

  if (collapsed) {
    const label = (point: RoutePoint) =>
      point.preset?.result === point.value ? point.preset.label : (point.value?.fullAddress ?? '…');
    return (
      <>
        <SummaryRow onClick={() => (isPicking && pick ? onPickToggle?.(pick.target) : setCollapsed(false))}>
          <SummaryText>
            {isPicking
              ? 'Кликните по карте, чтобы выбрать точку'
              : `${label(points[0])} → ${label(points[points.length - 1])}`}
            {!isPicking && selected && (
              <strong>
                {' · '}
                {formatKm(selected.distanceKm)} · {formatDuration(selected.durationSec)}
              </strong>
            )}
          </SummaryText>
          <Button type="button" variant="plain" color="primary">
            {isPicking ? 'Отмена' : 'Изменить'}
          </Button>
        </SummaryRow>
      </>
    );
  }

  return (
    <Panel>
      <Column>
        {points.map((point, index) => {
          const isFirst = index === 0;
          const isLast = index === points.length - 1;
          const pointCoords = coords[index];
          const picking = pick?.target === point.id ? pick.state : null;
          return (
            <React.Fragment key={point.id}>
              <FieldRow>
                <SearchBox
                  icon={
                    <PointDot color={isFirst ? POINT_COLORS.start : isLast ? POINT_COLORS.end : POINT_COLORS.via} />
                  }
                  placeholder={isFirst ? 'Откуда' : isLast ? 'Куда' : 'Через'}
                  near={near}
                  showFeedback={false}
                  preset={point.preset}
                  onSelect={result => setPointValue(point.id, result)}
                  onClear={() => setPointValue(point.id, null)}
                />
                {onPickToggle && (
                  <Button
                    type="button"
                    iconOnly
                    variant={picking != null ? 'standard' : 'outlined'}
                    color={picking != null ? 'primary' : 'default'}
                    title="Выбрать точку на карте"
                    onClick={() => onPickToggle(point.id)}
                  >
                    <PinIcon />
                  </Button>
                )}
                {points.length > 2 && (
                  <Button
                    type="button"
                    iconOnly
                    variant="plain"
                    title="Убрать точку"
                    onClick={() => removePoint(point.id)}
                  >
                    ×
                  </Button>
                )}
              </FieldRow>
              {picking === 'picking' ? (
                <PointHint>Кликните по карте, чтобы выбрать точку (Esc - отмена)</PointHint>
              ) : picking === 'resolving' ? (
                <PointHint>Определяем адрес…</PointHint>
              ) : (
                point.value &&
                (pointCoords ? (
                  // полный адрес - если в поле не он же (или не его хвост:
                  // "г. Екатеринбург" при адресе "..., г. Екатеринбург")
                  !(point.preset?.result === point.value &&
                    point.value.fullAddress.endsWith(point.preset.label)) && (
                    <PointHint>{point.value.fullAddress}</PointHint>
                  )
                ) : (
                  <PointError>У этого адреса нет координат</PointError>
                ))
              )}
            </React.Fragment>
          );
        })}

        <Row>
          <Button
            type="button"
            variant="plain"
            color="primary"
            startIcon={<PlusIcon />}
            onClick={addPoint}
            disabled={points.length >= MAX_POINTS}
          >
            Добавить точку
          </Button>
          <Button
            type="button"
            variant="plain"
            color="primary"
            startIcon={<SwapIcon />}
            onClick={reversePoints}
            title="Поменять начало и конец местами"
          >
            Поменять местами
          </Button>
          {route && (
            <Button
              type="button"
              variant="plain"
              color="primary"
              className="mobile-only"
              onClick={() => setCollapsed(true)}
            >
              ▲ Свернуть
            </Button>
          )}
        </Row>

        <Row>
          <Button
            type="button"
            variant={profile === 'truck' ? 'standard' : 'outlined'}
            color={profile === 'truck' ? 'primary' : 'default'}
            startIcon={<TruckIcon />}
            onClick={() => setProfile('truck')}
          >
            Грузовик
          </Button>
          <Button
            type="button"
            variant={profile === 'auto' ? 'standard' : 'outlined'}
            color={profile === 'auto' ? 'primary' : 'default'}
            startIcon={<CarIcon />}
            onClick={() => setProfile('auto')}
          >
            Авто
          </Button>
        </Row>

        {profile === 'truck' && (
          <Row>
            <TextField
              label="Высота, м"
              inputMode="decimal"
              value={height}
              onChange={e => setHeight(e.target.value)}
              placeholder="4"
              fullWidth
            />
            <TextField
              label="Масса, т"
              inputMode="decimal"
              value={weight}
              onChange={e => setWeight(e.target.value)}
              placeholder="40"
              fullWidth
            />
            <TextField
              label="На ось, т"
              inputMode="decimal"
              value={axleLoad}
              onChange={e => setAxleLoad(e.target.value)}
              placeholder="10"
              fullWidth
            />
          </Row>
        )}

        <Selectbox<BorderOption>
          items={BORDER_OPTIONS}
          value={BORDER_OPTIONS.find(option => option.value === borders) ?? null}
          isOpen={bordersOpen}
          onRequestOpen={() => setBordersOpen(true)}
          onRequestClose={() => setBordersOpen(false)}
          onChange={option => {
            if (option) setBorders(option.value);
            setBordersOpen(false);
          }}
          selectedItemToString={option => option.label}
          startIcon={<BorderIcon />}
          getOptionSelected={({ item, value }) => item.value === value.value}
          fullWidth
        >
          {({ item }, itemProps) => (
            <SelectboxItem {...itemProps} key={item.value}>
              {item.label}
            </SelectboxItem>
          )}
        </Selectbox>

        <Row>
          <Switch checked={avoidTolls} onChange={e => setAvoidTolls(e.target.checked)}>
            Без платных дорог
          </Switch>
          <Switch checked={avoidFerries} onChange={e => setAvoidFerries(e.target.checked)}>
            Без паромов
          </Switch>
        </Row>

        {isLoading && <Muted>Строим маршрут…</Muted>}
        {error && !isLoading && <ErrorText>{error}</ErrorText>}

        {route && selected && !isLoading && (
          <Result>
            {variants.length > 1 && (
              <Row>
                {variants.map((v, i) => (
                  <Button
                    key={i}
                    type="button"
                    variant={i === variant ? 'standard' : 'outlined'}
                    color={i === variant ? 'primary' : 'default'}
                    onClick={() => setVariant(i)}
                  >
                    {formatKm(v.distanceKm)} · {formatDuration(v.durationSec)}
                    {v.tollDistanceKm > 0 ? ' · платно' : ''}
                  </Button>
                ))}
              </Row>
            )}
            <Headline>
              {formatKm(selected.distanceKm)} · {formatDuration(selected.durationSec)}
            </Headline>
            <Muted>
              {selected.countries.map(c => `${c.name} · ${formatKm(c.distanceKm)}`).join(' → ')}
            </Muted>
            {selected.tollDistanceKm > 0 ? (
              <TollLegend>
                <TollSwatch />
                Платные участки: {formatKm(selected.tollDistanceKm)}
              </TollLegend>
            ) : (
              // старый бэкенд без tollSections - хотя бы сам факт
              selected.hasToll && <Muted>есть платные участки</Muted>
            )}
            {selected.hasFerry && <Muted>есть паром</Muted>}
            <Muted>{selected.regions.map(r => r.name).join(' → ')}</Muted>
            <StepsButton
              type="button"
              variant="outlined"
              color="primary"
              startIcon={<ListIcon />}
              endIcon={<StepsChevron open={showManeuvers} />}
              onClick={() => setShowManeuvers(v => !v)}
            >
              Маршрут по шагам · {maneuvers.length}
            </StepsButton>
            {showManeuvers && (
              <Maneuvers>
                {maneuvers.map((m, i) => (
                  <li key={i}>
                    {m.instruction}
                    {m.distanceKm > 0 && <Muted as="span"> — {formatKm(m.distanceKm)}</Muted>}
                  </li>
                ))}
              </Maneuvers>
            )}
          </Result>
        )}
      </Column>
    </Panel>
  );
};

export default RoutePanel;
